import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { mediaUrl, PLACEHOLDER_COVER } from "@/lib/media";
import { toast } from "sonner";
import PortalShell from "@/portal/PortalShell";
import { usePortal } from "@/portal/PortalContext";
import { Button } from "@/portal/ui/Button";
import { Skeleton, Progress } from "@/portal/ui/Layout";
import { Tabs, TabPanel, useTabParam } from "@/portal/ui/Tabs";
import { StatusBadge, PROJECT_STATUS } from "@/portal/ui/Badge";
import { fmtDateHuman } from "@/portal/format";
import { Mail, FileText, CalendarDays, Scan, Layers, MapPin, Users2 } from "lucide-react";
import OverviewTab from "./project/OverviewTab";
import DocumentsTab from "./project/DocumentsTab";
import BudgetTab from "./project/BudgetTab";
import CalendarTab from "./project/CalendarTab";
import GovernanceTab from "./project/GovernanceTab";
import RiskTab from "./project/RiskTab";
import QualityTab from "./project/QualityTab";
import GalleryTab from "./project/GalleryTab";
import TeamTab from "./project/TeamTab";
import JournalTab from "./project/JournalTab";
import { ContactPmModal } from "./project/PmCard";

const STAGE_RO = { Concept: "Concept", Design: "Proiectare", Tender: "Licitație & contractare", Execution: "Execuție", Handover: "Predare" };

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refresh: refreshOverview } = usePortal();
  const [data, setData] = useState(null);
  const [contact, setContact] = useState(false);

  const load = useCallback(async () => {
    try {
      const p = await api.get(`/projects/${id}`);
      const [m, d, ph, c, portal] = await Promise.all([
        api.get(`/projects/${id}/milestones`), api.get(`/projects/${id}/documents`),
        api.get(`/projects/${id}/photos`), api.get(`/projects/${id}/contacts`),
        api.get(`/client/projects/${id}/portal`).catch(() => ({ data: null })),
      ]);
      setData({ project: p.data, milestones: m.data, documents: d.data, photos: ph.data, contacts: c.data, portal: portal.data });
    } catch (e) {
      const code = e?.response?.status;
      toast.error(code === 403 ? "Nu ai acces la acest proiect" : code === 404 ? "Proiectul nu există" : "Proiectul nu poate fi încărcat");
      navigate("/portal/proiecte", { replace: true });
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const project = data?.project;
  const portal = data?.portal;
  const pending = (portal?.actions || []).filter((a) => a.status === "pending").length;
  const budgetVisible = portal?.budget?.visible !== false;

  const tabs = [
    { id: "prezentare", label: "Prezentare" },
    { id: "documente", label: "Documente", count: data?.documents?.length || undefined, countTone: "neutral", hidden: project?.show_documents === false },
    { id: "buget", label: "Buget & costuri", hidden: !budgetVisible },
    { id: "calendar", label: "Calendar" },
    { id: "decizii", label: "Decizii & ședințe", count: pending || undefined },
    { id: "risc", label: "Risc" },
    { id: "calitate", label: "Calitate" },
    { id: "galerie", label: "Galerie", hidden: project?.show_gallery === false },
    { id: "echipa", label: "Echipă", hidden: project?.show_team === false },
    { id: "jurnal", label: "Jurnal de proiect" },
  ];
  const [rawTab, setTab] = useTabParam("prezentare", [...tabs.map((t) => t.id), "etape"]);
  const tab = rawTab === "etape" ? "calendar" : rawTab;
  const onChanged = async () => { await load(); refreshOverview(); };

  const currentStage = portal?.current_stage || project?.current_stage || "";
  const stageObj = (portal?.timeline || []).find((s) => s.status === "In Progress");
  const stageLabel = stageObj ? STAGE_RO[stageObj.stage] || stageObj.stage : "";
  const stageIdx = (portal?.timeline || []).findIndex((s) => s.status === "In Progress");
  const decorIndex = String((stageIdx >= 0 ? stageIdx : 0) + 1).padStart(2, "0");
  const clientName = project?.company?.name || project?.client_name || "—";

  return (
    <PortalShell title={project?.name || "Proiect"} crumbs={[{ label: "Proiecte", to: "/portal/proiecte" }, { label: project?.name || "…" }]}>
      {!data ? <DetailSkeleton /> : (
        <>
          <header className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-5">
            <div className="min-w-0">
              <div className="cp-mono uppercase mb-1.5" data-testid="project-code">{project.code}</div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="cp-display" data-testid="project-title">{project.name}</h1>
                <StatusBadge map={PROJECT_STATUS} value={project.client_status} data-testid="project-status" noIcon />
              </div>
              <div className="mt-2.5 flex items-center gap-x-5 gap-y-1.5 flex-wrap text-[13px] text-cp-text-2">
                {project.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-cp-text-3" aria-hidden="true" />{project.location}</span>}
                {project.surface && <span className="inline-flex items-center gap-1.5"><Scan className="h-4 w-4 text-cp-text-3" aria-hidden="true" />{project.surface}</span>}
                {(stageLabel || currentStage) && <span className="inline-flex items-center gap-1.5"><FileText className="h-4 w-4 text-cp-text-3" aria-hidden="true" />{stageLabel || currentStage}</span>}
              </div>
            </div>
            <div className="hidden lg:flex flex-col items-end shrink-0 pt-1">
              <div className="flex items-baseline gap-3">
                {project.tagline && <span className="cp-decor-tagline text-right max-w-[220px]">{project.tagline}</span>}
                <span className="cp-decor-index" aria-hidden="true">/{decorIndex}</span>
              </div>
              {project.pm && <Button variant="outline" onClick={() => setContact(true)} data-testid="contact-pm-btn" className="mt-4"><Mail /> Contactează managerul</Button>}
            </div>
          </header>

          {/* Hero */}
          <div className="relative rounded-[12px] overflow-hidden bg-cp-bg-subtle aspect-[16/9] sm:aspect-[21/9] lg:aspect-[21/7] border border-cp-border" data-testid="project-cover">
            <svg className="cp-hero-accent" width="34" height="34" viewBox="0 0 34 34" fill="none" aria-hidden="true">
              <path d="M0 0h16L6 16H0V0z" fill="#D97706" />
              <path d="M13 0h11L11 20H4L13 0z" fill="#0E2A3F" opacity="0.9" />
            </svg>
            <img src={mediaUrl(project.cover_image) || PLACEHOLDER_COVER} alt={`Imagine proiect ${project.name}`} className="h-full w-full object-cover" />
            {project.company?.logo_url && (
              <div className="absolute top-4 right-4 bg-white/95 rounded-[var(--cp-radius)] px-3 py-2 shadow-[var(--cp-shadow-sm)]" data-testid="company-badge">
                <img src={mediaUrl(project.company.logo_url)} alt={project.company.name} className="h-7 w-auto max-w-[140px] object-contain" />
              </div>
            )}
          </div>

          {/* KPI bar + pull quote */}
          <div className="mt-4 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_260px] gap-4 items-stretch">
            <dl className="cp-panel grid grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-cp-border [&>div:nth-child(1)]:border-r sm:[&>div]:border-0 lg:[&>div:not(:last-child)]:border-r [&>div]:border-cp-border" data-testid="governance-strip">
              <div className="px-5 py-4 flex items-center gap-4">
                <span className="cp-metric-lg text-cp-accent">{project.progress}%</span>
                <div className="min-w-0 flex-1"><dt className="text-[12.5px] text-cp-text-2">Progres general</dt><dd className="mt-1.5"><Progress value={project.progress} tone={project.client_status === "Delayed" ? "danger" : project.client_status === "Attention Required" ? "warning" : undefined} className="max-w-[150px]" /></dd></div>
              </div>
              <Stat icon={CalendarDays} value={fmtDateHuman(portal?.estimated_completion || project.end_date)} label="Finalizare estimată" />
              <Stat icon={project.surface ? Scan : Layers} value={project.surface || project.sector || "—"} label={project.surface ? "Suprafață" : "Sector"} />
              <Stat icon={Users2} value={clientName} label="Client" />
            </dl>
            <div className="cp-panel px-5 py-4 flex items-center justify-center xl:justify-start" data-testid="hero-quote">
              <p className="cp-serif italic text-[15px] leading-snug text-cp-text-2">
                Partnering with clients to shape and deliver ambitious projects across real estate, infrastructure, retail, energy and industry.
              </p>
            </div>
          </div>

          <div className="mt-6">
            <Tabs items={tabs} value={tab} onChange={setTab} ariaLabel="Secțiunile proiectului" className="-mx-4 px-4 sm:mx-0 sm:px-0" />
            <div className="pt-5">
              <TabPanel id="prezentare" active={tab === "prezentare"}><OverviewTab project={project} portal={portal} milestones={data.milestones} onGoTo={setTab} /></TabPanel>
              <TabPanel id="documente" active={tab === "documente"}><DocumentsTab documents={data.documents} project={project} onContact={() => setContact(true)} onChanged={onChanged} /></TabPanel>
              <TabPanel id="buget" active={tab === "buget"}><BudgetTab portal={portal} project={project} /></TabPanel>
              <TabPanel id="calendar" active={tab === "calendar"}><CalendarTab portal={portal} project={project} milestones={data.milestones} /></TabPanel>
              <TabPanel id="decizii" active={tab === "decizii"}><GovernanceTab portal={portal} project={project} documents={data.documents} onChanged={onChanged} /></TabPanel>
              <TabPanel id="risc" active={tab === "risc"}><RiskTab portal={portal} /></TabPanel>
              <TabPanel id="calitate" active={tab === "calitate"}><QualityTab portal={portal} /></TabPanel>
              <TabPanel id="galerie" active={tab === "galerie"}><GalleryTab photos={data.photos} project={project} /></TabPanel>
              <TabPanel id="echipa" active={tab === "echipa"}><TeamTab project={project} contacts={data.contacts} onContact={() => setContact(true)} /></TabPanel>
              <TabPanel id="jurnal" active={tab === "jurnal"}><JournalTab portal={portal} documents={data.documents} photos={data.photos} milestones={data.milestones} /></TabPanel>
            </div>
          </div>
          <ContactPmModal pm={project.pm} project={project} open={contact} onOpenChange={setContact} />
        </>
      )}
    </PortalShell>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="px-5 py-4 flex items-center gap-3 min-w-0">
      <Icon className="h-6 w-6 text-cp-text-2 shrink-0" aria-hidden="true" />
      <div className="min-w-0"><dd className="cp-serif text-[19px] text-cp-text leading-tight truncate">{value}</dd><dt className="text-[12.5px] text-cp-text-2 mt-0.5 truncate">{label}</dt></div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div aria-busy="true" className="space-y-5">
      <Skeleton className="h-12 w-2/3" />
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-5"><Skeleton className="xl:col-span-3 aspect-[16/5] w-full" /><Skeleton className="h-[260px]" /></div>
      <Skeleton className="h-10 w-full" />
    </div>
  );
}
